import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, TextField,
  MenuItem, CircularProgress, Paper, InputAdornment, Alert
} from '@mui/material';
import {
  Refresh as RefreshIcon, Person as PersonIcon, Search as SearchIcon, FilterList as FilterIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getWorkshopQueue, assignTechnician, getInhouseTechnicians, getTechnicians, deleteRepair } from '../../services/api';
import { useBranchFilter } from '../../context/BranchContext';
import { canDelete } from '../../utils/auth';
import { getErrorMessage } from '../../utils/errorMessage';
import ExportButtons from '../../components/ExportButtons';
import { dash, fmtExportStatus } from '../../utils/tableExport';

const STATUS_OPTIONS = ['', 'pending', 'diagnosis', 'quoted', 'approved', 'in_progress'];
const PRIORITY_OPTIONS = ['', 'low', 'medium', 'high', 'urgent'];
const REPAIR_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'in_house', label: 'In-house' },
  { value: 'outsource', label: 'Outsource' },
  { value: 'both', label: 'Both' }
];

const RepairWorkshop = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranchFilter();
  const [queue, setQueue] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState(null);
  const [selectedTech, setSelectedTech] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    technician_name: '',
    repair_location_type: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
    const onBranchChange = () => loadData();
    window.addEventListener('branch-filter-changed', onBranchChange);
    return () => window.removeEventListener('branch-filter-changed', onBranchChange);
  }, [filters.status, filters.priority, filters.technician_name, filters.repair_location_type, selectedBranchId]);

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.technician_name) params.set('technician_name', filters.technician_name);
    if (filters.repair_location_type) params.set('repair_location_type', filters.repair_location_type);
    if (filters.search) params.set('search', filters.search);
    if (selectedBranchId) params.set('branch_id', selectedBranchId);
    const q = params.toString();
    return q ? `?${q}` : '';
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [q, inhouse] = await Promise.all([
        getWorkshopQueue(buildQuery()),
        getInhouseTechnicians().catch(() => ({ data: [] }))
      ]);
      setQueue(q.data || []);
      let techs = inhouse.data || [];
      if (!techs.length) {
        const t = await getTechnicians().catch(() => ({ data: [] }));
        techs = t.data || [];
      }
      setTechnicians(techs);
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err, 'Failed to load workshop queue'));
    } finally {
      setLoading(false);
    }
  };

  const handleAutoAssign = async (id) => {
    await assignTechnician(id, { auto_assign: true });
    loadData();
  };

  const handleDeleteJob = async (r) => {
    if (!canDelete()) return;
    const label = r.grn_number || r.service_number || r.repair_number || `#${r.id}`;
    if (!window.confirm(`Delete workshop job ${label}?\nThis cannot be undone.`)) return;
    try {
      await deleteRepair(r.id);
      setSuccess(`Deleted ${label}`);
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete job'));
    }
  };

  const handleManualAssign = async () => {
    const tech = technicians.find((t) => String(t.id) === String(selectedTech));
    await assignTechnician(assignDialog, {
      technician_id: parseInt(selectedTech, 10),
      technician_name: tech?.name
    });
    setAssignDialog(null);
    loadData();
  };

  const filteredQueue = useMemo(() => {
    if (!filters.search.trim()) return queue;
    const q = filters.search.toLowerCase();
    return queue.filter((r) =>
      (r.service_number || '').toLowerCase().includes(q) ||
      (r.repair_number || '').toLowerCase().includes(q) ||
      (r.customer_name || '').toLowerCase().includes(q) ||
      (r.technician_name || '').toLowerCase().includes(q)
    );
  }, [queue, filters.search]);

  const priorityIcon = (p) => (p === 'high' || p === 'urgent' ? '🚨' : '');

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>🔧 Workshop Management</Typography>
          <Typography color="text.secondary">Filter by repair type, technician, priority, status and assign jobs</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <ExportButtons
            title="Workshop Queue"
            filename="workshop_queue"
            landscape
            columns={[
              { label: 'JOB #', value: (r) => dash(r.grn_number) },
              { label: 'Service #', value: (r) => dash(r.service_number || r.repair_number) },
              { label: 'Customer', value: (r) => dash(r.customer_name) },
              { label: 'Device', value: (r) => [r.device_brand, r.device_model].filter(Boolean).join(' ') || '-' },
              { label: 'Issue', value: (r) => dash(r.device_issue) },
              { label: 'Status', value: (r) => fmtExportStatus(r.status) },
              { label: 'Priority', value: (r) => fmtExportStatus(r.priority) },
              { label: 'Type', value: (r) => fmtExportStatus(r.repair_location_type || 'in_house') },
              { label: 'Technician', value: (r) => dash(r.outsource_technician_name || r.technician_name || 'Unassigned') }
            ]}
            rows={filteredQueue}
            onError={setError}
          />
          <Button startIcon={<RefreshIcon />} onClick={loadData}>Refresh</Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterIcon color="action" />
          <Typography variant="subtitle1" fontWeight={600}>Filters</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Service #, customer, tech..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth select size="small" label="Repair type" value={filters.repair_location_type}
              onChange={(e) => setFilters({ ...filters, repair_location_type: e.target.value })}>
              {REPAIR_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth select size="small" label="Status" value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <MenuItem value="">All Statuses</MenuItem>
              {STATUS_OPTIONS.filter(Boolean).map((s) => (
                <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={1.5}>
            <TextField fullWidth select size="small" label="Priority" value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              {PRIORITY_OPTIONS.filter(Boolean).map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth select size="small" label="Technician" value={filters.technician_name}
              onChange={(e) => setFilters({ ...filters, technician_name: e.target.value })}>
              <MenuItem value="">All Technicians</MenuItem>
              {technicians.map((t) => (
                <MenuItem key={t.id} value={t.name}>{t.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={1.5}>
            <Button fullWidth variant="outlined" onClick={() => setFilters({ search: '', status: '', priority: '', technician_name: '', repair_location_type: '' })}>
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? <CircularProgress /> : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {filteredQueue.length} job(s)
          </Typography>
          <Grid container spacing={2}>
            {filteredQueue.map((r) => (
              <Grid item xs={12} md={6} key={r.id}>
                <Card sx={{ borderLeft: r.priority === 'high' || r.priority === 'urgent' ? '4px solid #e53935' : '4px solid #ccc' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6">{r.service_number || r.repair_number}</Typography>
                      <Box>
                        {r.grn_number && <Chip label={r.grn_number} size="small" color="primary" sx={{ mr: 0.5, fontFamily: 'monospace' }} />}
                        <Chip label={r.status?.replace(/_/g, ' ')} size="small" />
                      </Box>
                    </Box>
                    <Typography variant="body2"><strong>{r.customer_name}</strong> — {r.device_brand} {r.device_model}</Typography>
                    <Typography variant="body2" color="text.secondary">{r.device_issue?.slice(0, 80)}</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip label={`${r.priority} ${priorityIcon(r.priority)}`} size="small"
                        color={r.priority === 'high' || r.priority === 'urgent' ? 'error' : 'default'} sx={{ mr: 1 }} />
                      <Chip
                        label={
                          r.repair_location_type === 'outsource'
                            ? 'Outsource'
                            : r.repair_location_type === 'both'
                              ? 'Both'
                              : 'In-house'
                        }
                        size="small"
                        color={
                          r.repair_location_type === 'outsource'
                            ? 'secondary'
                            : r.repair_location_type === 'both'
                              ? 'warning'
                              : 'success'
                        }
                        sx={{ mr: 1 }}
                      />
                      {r.technician_name || r.outsource_technician_name
                        ? <Chip icon={<PersonIcon />} label={r.outsource_technician_name || r.technician_name} size="small" />
                        : <Chip label="Unassigned" size="small" variant="outlined" />}
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" onClick={() => navigate(`/repairs/${r.id}`)}>View</Button>
                      <Button size="small" variant="contained" onClick={() => handleAutoAssign(r.id)}>Auto Assign</Button>
                      <Button size="small" onClick={() => { setAssignDialog(r.id); setSelectedTech(''); }}>Manual Assign</Button>
                      {canDelete() && (
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteJob(r)}>
                          Delete
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {filteredQueue.length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No jobs match your filters</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </>
      )}

      {assignDialog && (
        <Paper sx={{ position: 'fixed', bottom: 24, right: 24, p: 3, zIndex: 1300, minWidth: 280, boxShadow: 6 }}>
          <Typography variant="h6" gutterBottom>Assign Technician</Typography>
          <TextField fullWidth select label="Technician" sx={{ mb: 2 }} value={selectedTech} onChange={(e) => setSelectedTech(e.target.value)}>
            {technicians.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setAssignDialog(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleManualAssign}>Assign</Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default RepairWorkshop;
